import { SiteOption } from "../../types/SiteOption"
import { BrandOption } from "../../types/BrandOption"

export type ModuleVOTab = 'variableFees' | 'fixedFees' | 'marginValuation'
export type ModuleVOConfigScope = 'brand' | 'site' | 'global'

/** Identifiants des onglets Bootstrap de la page Module VO, par onglet fonctionnel. */
export const MODULE_VO_TAB_PANE_IDS: Record<ModuleVOTab, string> = {
    variableFees: 'fraisVariables',
    fixedFees: 'fraisFixes',
    marginValuation: 'marge',
}

/**
 * URL de retour vers la page Module VO en réaffichant l'onglet d'où l'on vient. On passe par un
 * paramètre de query (et non le hash) car le hash est déjà utilisé par les onglets Bootstrap.
 */
export function buildModuleVOOverviewUrl(tab: ModuleVOTab): string {
    return `/moduleVO?tab=${tab}`
}

export function parseModuleVOTab(value: string | null): ModuleVOTab | null {
    return value === 'variableFees' || value === 'fixedFees' || value === 'marginValuation' ? value : null
}

export function buildModuleVOEditUrl(tab: ModuleVOTab, params: { brand?: string, dealershipIds?: number[] }): string {
    const searchParams = new URLSearchParams()

    if (params.brand) {
        searchParams.set('brand', params.brand)
    }

    if (params.dealershipIds && params.dealershipIds.length > 0) {
        const key = params.dealershipIds.length > 1 ? 'dealershipIds' : 'dealershipId'
        searchParams.set(key, params.dealershipIds.join(','))
    }

    const query = searchParams.toString()
    const path = `/moduleVO/edit/${tab}`
    return query ? `${path}?${query}` : path
}

export function buildModuleVOConfigUrl(tab: ModuleVOTab, scope: ModuleVOConfigScope): string {
    return `/moduleVO/config/${tab}?scope=${scope}`
}

export interface SaveTarget {
    dealershipIds: number[]
    brands: string[]
}

/**
 * Détermine la cible d'enregistrement à partir des sélections des ScopeSelectors. Les deux
 * listes représentent directement la cible (primaire + configuration avancée éventuelle,
 * quelle que soit la portée) : pas besoin de distinguer "site" / "brand" ici, ni de gérer un
 * scope "global" à part — sites et marques y sont naturellement vides.
 */
export function computeSaveTarget(selectedSites: SiteOption[], selectedBrands: BrandOption[]): SaveTarget {
    return {
        dealershipIds: selectedSites.map(site => site.value),
        brands: selectedBrands.map(brand => brand.value),
    }
}

/**
 * Construit la query string ciblant un ou plusieurs sites et/ou une ou plusieurs marques.
 * Un seul élément -> paramètre singulier (dealership_id, brand), plusieurs -> paramètre
 * pluriel et liste séparée par des virgules (dealershipIds, brands), à charge pour l'API
 * de gérer ces deux formes (actuellement elle ne gère qu'un seul dealership_id).
 */
export function buildSaveTargetQuery(target: SaveTarget): string {
    const params = new URLSearchParams()

    if (target.dealershipIds.length === 1) {
        params.set('dealership_id', String(target.dealershipIds[0]))
    } else if (target.dealershipIds.length > 1) {
        params.set('dealershipIds', target.dealershipIds.join(','))
    }

    if (target.brands.length === 1) {
        params.set('brand', target.brands[0])
    } else if (target.brands.length > 1) {
        params.set('brands', target.brands.join(','))
    }

    const query = params.toString()
    return query ? `?${query}` : ''
}
